import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnnouncementBody } from "./markdown";

/**
 * THE "WHAT'S NEW" POP-UP (spec: docs/announcements/ANNOUNCEMENTS_SPEC.md).
 *
 * Fixed header (label + title) and footer (Remind me later · Got it ·
 * action button); only the body scrolls, so the way out and the action are
 * always on screen however long the content is. A fade and a "More below"
 * chip show until the reader reaches the end (reported once via
 * onReachedEnd). Up to 70% of the window on desktop; a full-screen sheet on
 * phones. `preview` renders it inside its parent (Company Settings editor)
 * instead of over the page, and never reports events.
 */
export type AnnouncementView = {
  title: string;
  body: string;
  actionLabel?: string | null;
  actionPath?: string | null;
  moreUrl?: string | null;
};

type Props = {
  announcement: AnnouncementView;
  onLater?: () => void;
  onGotIt?: () => void;
  onAction?: () => void;
  onClose?: () => void;
  onReachedEnd?: () => void;
  onLink?: (href: string) => void;
  /** Editor preview: render in place at this device width. */
  preview?: "desktop" | "phone";
};

const BLACK = "#111111";

const AnnouncementModal: React.FC<Props> = ({ announcement, onLater, onGotIt, onAction, onClose, onReachedEnd, onLink, preview }) => {
  const scroller = useRef<HTMLDivElement | null>(null);
  const [atEnd, setAtEnd] = useState(false);
  const reported = useRef(false);
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 640 : false));
  const phone = preview ? preview === "phone" : narrow;

  const check = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const end = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    setAtEnd(end);
    if (end && !reported.current) {
      reported.current = true;
      onReachedEnd?.();
    }
  }, [onReachedEnd]);

  useEffect(() => {
    check();
  }, [announcement.body, check]);

  useEffect(() => {
    if (preview) return;
    const onResize = () => setNarrow(window.innerWidth < 640);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [preview, onClose]);

  const fs = phone ? 15 : 14;
  const btn = (primary: boolean): React.CSSProperties => ({
    border: primary ? 0 : "1px solid #D6D6D3",
    background: primary ? BLACK : "#FFFFFF",
    color: primary ? "#FFFFFF" : BLACK,
    fontFamily: "Poppins, sans-serif",
    fontSize: phone ? 15 : 13,
    fontWeight: 500,
    borderRadius: phone ? 8 : 6,
    padding: phone ? "14px" : "11px 18px",
    cursor: "pointer",
    width: phone ? "100%" : undefined,
  });
  const later: React.CSSProperties = { border: 0, background: "transparent", color: "#444444", fontFamily: "Poppins, sans-serif", fontSize: phone ? 14 : 13, padding: "12px 4px", cursor: "pointer" };

  const card = (
    <div
      role="dialog"
      aria-modal={preview ? undefined : true}
      aria-labelledby="announcement-title"
      style={{
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "Poppins, sans-serif",
        color: BLACK,
        ...(phone
          ? { width: "100%", height: "100%", borderRadius: preview ? 12 : 0 }
          : { width: "100%", maxWidth: 560, maxHeight: preview ? "100%" : "70vh", borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,0.28)" }),
      }}
    >
      <div style={{ padding: phone ? "20px 20px 14px" : "22px 24px 16px", borderBottom: "1px solid #ECECEA", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexShrink: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <span style={{ alignSelf: "flex-start", background: "#EEF4FB", color: "#185FA5", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, letterSpacing: "0.4px" }}>{"WHAT'S NEW"}</span>
          <h2 id="announcement-title" style={{ margin: 0, fontFamily: "Rubik, Poppins, sans-serif", fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>
            {announcement.title || "Untitled"}
          </h2>
        </div>
        <button type="button" aria-label="Close" onClick={onClose} style={{ width: 44, height: 44, flexShrink: 0, border: 0, background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", margin: "-10px -10px 0 0" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#444444" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <div style={{ position: "relative", flex: "1 1 auto", minHeight: 0, display: "flex" }}>
        <div ref={scroller} onScroll={check} style={{ flex: 1, overflowY: "auto", padding: phone ? "16px 20px 40px" : "16px 24px 40px", fontSize: fs, lineHeight: 1.55, color: "#222222" }}>
          <AnnouncementBody md={announcement.body} fontSize={fs} onLink={onLink} />
          {announcement.moreUrl ? (
            <a
              href={announcement.moreUrl}
              target={announcement.moreUrl.startsWith("/") ? undefined : "_blank"}
              rel="noopener noreferrer"
              onClick={(e) => {
                const url = announcement.moreUrl ?? "";
                if (url.startsWith("/") && onLink) {
                  e.preventDefault();
                  onLink(url);
                }
              }}
              style={{ fontSize: fs - 1 }}
            >
              Read more
            </a>
          ) : null}
        </div>
        {!atEnd && (
          <>
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(rgba(255,255,255,0), #FFFFFF)", pointerEvents: "none" }} />
            <button
              type="button"
              onClick={() => scroller.current?.scrollBy({ top: (scroller.current?.clientHeight ?? 200) * 0.8, behavior: "smooth" })}
              style={{ position: "absolute", left: "50%", bottom: 10, transform: "translateX(-50%)", background: BLACK, color: "#FFFFFF", fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 500, border: 0, borderRadius: 999, padding: "5px 12px", display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}
            >
              More below
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </>
        )}
      </div>

      <div style={{ padding: phone ? "12px 20px 24px" : "14px 24px", borderTop: "1px solid #ECECEA", display: "flex", flexDirection: phone ? "column" : "row", justifyContent: "space-between", alignItems: phone ? "stretch" : "center", gap: phone ? 8 : 12, flexShrink: 0 }}>
        {phone ? (
          <>
            {announcement.actionLabel ? (
              <button type="button" style={btn(true)} onClick={onAction}>
                {announcement.actionLabel}
              </button>
            ) : null}
            <button type="button" style={btn(!announcement.actionLabel)} onClick={onGotIt}>
              Got it
            </button>
            <button type="button" style={later} onClick={onLater}>
              Remind me later
            </button>
          </>
        ) : (
          <>
            <button type="button" style={later} onClick={onLater}>
              Remind me later
            </button>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" style={btn(!announcement.actionLabel)} onClick={onGotIt}>
                Got it
              </button>
              {announcement.actionLabel ? (
                <button type="button" style={btn(true)} onClick={onAction}>
                  {announcement.actionLabel}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (preview) return card;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(17,17,17,0.55)", display: "flex", alignItems: phone ? "stretch" : "center", justifyContent: "center", padding: phone ? 0 : 24 }}>
      {card}
    </div>
  );
};

export default AnnouncementModal;
